(() => {
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-ablp-results]').forEach((section) => {
      if (section.dataset.ablpReady) return;
      section.dataset.ablpReady = 'true';
      const viewport = section.querySelector('[data-ablp-results-track]');
      const cards = [...section.querySelectorAll('[data-ablp-results-card]')];
      const previous = section.querySelector('[data-ablp-results-prev]');
      const next = section.querySelector('[data-ablp-results-next]');
      const status = section.querySelector('[data-ablp-results-status]');
      const controls = section.querySelector('[data-ablp-results-controls]');
      if (!viewport || !cards.length) return;
      let frame = 0;
      const update = () => {
        frame = 0;
        const box = viewport.getBoundingClientRect();
        const visible = cards.filter((card) => {
          const rect = card.getBoundingClientRect();
          return rect.left >= box.left - 4 && rect.left < box.right - 30;
        });
        const index = visible.length ? cards.indexOf(visible[0]) : 0;
        const last = visible.length ? cards.indexOf(visible[visible.length - 1]) : index;
        const pad = (value) => String(value).padStart(2, '0');
        const separator = section.dataset.ablpRangeSeparator ?? '–';
        const current = `${pad(index + 1)}${last > index ? `${separator}${pad(last + 1)}` : ''}`;
        const position = (section.dataset.ablpCounterFormat ?? '[current] / [total]').replaceAll('[current]', current).replaceAll('[total]', pad(cards.length));
        if (status.textContent !== position) status.textContent = position;
        previous.disabled = viewport.scrollLeft <= 2;
        next.disabled = viewport.scrollLeft >= viewport.scrollWidth - viewport.clientWidth - 2;
        controls.hidden = viewport.scrollWidth <= viewport.clientWidth + 2;
      };
      const requestUpdate = () => { if (!frame) frame = requestAnimationFrame(update); };
      const move = (direction) => {
        const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : cards[0].offsetWidth;
        viewport.scrollBy({ left: direction * step, behavior: motion.matches ? 'instant' : 'smooth' });
      };
      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      viewport.addEventListener('scroll', requestUpdate, { passive: true });
      if ('ResizeObserver' in window) {
        const resize = new ResizeObserver(requestUpdate);
        resize.observe(viewport);
        section.ablpCleanup = () => { resize.disconnect(); cancelAnimationFrame(frame); };
      }
      update();
    });
    scope.querySelectorAll('[data-ablp-footer-end]').forEach((ending) => {
      if (ending.dataset.ablpReady) return;
      ending.dataset.ablpReady = 'true';
      const shape = ending.querySelector('[data-ablp-wordmark-shape]');
      const roundness = ending.querySelector('[data-ablp-wordmark-roundness]');
      const softness = ending.querySelector('[data-ablp-wordmark-softness]');
      if (!shape || !roundness || !softness) return;
      const enabled = ending.dataset.ablpMotion === 'true';
      const strength = Math.max(10, Math.min(40, Number(ending.dataset.ablpMorphStrength) || 20)) / 20;
      const speed = Math.max(.5, Math.min(1.5, (Number(ending.dataset.ablpMorphSpeed) || 80) / 100));
      let inView = !('IntersectionObserver' in window);
      let started = false;
      let completed = false;
      let running = false;
      let frame = 0;
      let elapsed = 0;
      let previousTime = 0;
      let lastPaint = 0;
      const settle = () => {
        shape.setAttribute('opacity', '1');
        shape.removeAttribute('transform');
        roundness.setAttribute('radius', '.35');
        softness.setAttribute('stdDeviation', '2.4');
        completed = true;
        running = false;
        ending.classList.remove('is-visible');
      };
      const draw = () => {
        const progress = Math.min(1, elapsed / 1500);
        const ease = 1 - Math.pow(1 - progress, 3);
        if (progress === 1) {
          settle();
          return;
        }
        roundness.setAttribute('radius', (.35 + (1 - ease) * .75 * strength).toFixed(3));
        softness.setAttribute('stdDeviation', (2.4 + (1 - ease) * .5 * strength).toFixed(3));
        shape.setAttribute('opacity', ease.toFixed(3));
        shape.setAttribute('transform', `translate(0 ${(10 * (1 - ease)).toFixed(2)})`);
      };
      const tick = (now) => {
        frame = 0;
        if (!running || !ending.isConnected) return;
        if (previousTime) elapsed += Math.min(now - previousTime, 100) * speed;
        previousTime = now;
        if (!lastPaint || now - lastPaint >= 1000 / 30 || elapsed >= 1500) {
          draw();
          lastPaint = now;
        }
        if (running) frame = requestAnimationFrame(tick);
      };
      const syncMotion = () => {
        if (!enabled || motion.matches) {
          settle();
          shape.removeAttribute('filter');
        }
        running = enabled && !completed && inView && !document.hidden && !motion.matches;
        ending.classList.toggle('is-visible', running);
        if (running && !started) {
          started = true;
          shape.setAttribute('filter', `url(#${shape.dataset.ablpFilterId})`);
          draw();
        }
        if (running && !frame) {
          previousTime = 0;
          lastPaint = 0;
          frame = requestAnimationFrame(tick);
        } else if (!running) {
          cancelAnimationFrame(frame);
          frame = 0;
          previousTime = 0;
        }
      };
      const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
        inView = entries[0].isIntersecting;
        syncMotion();
      }, { threshold: 0.05 }) : null;
      observer?.observe(ending);
      document.addEventListener('visibilitychange', syncMotion);
      motion.addEventListener('change', syncMotion);
      syncMotion();
      ending.ablpCleanup = () => {
        running = false;
        cancelAnimationFrame(frame);
        observer?.disconnect();
        document.removeEventListener('visibilitychange', syncMotion);
        motion.removeEventListener('change', syncMotion);
      };
    });
  };
  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-ablp-results], [data-ablp-footer-end]').forEach((section) => section.ablpCleanup?.());
  });
  document.addEventListener('shopify:block:select', (event) => {
    const card = event.target.closest('[data-ablp-results-card]');
    if (card) card.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'start' });
  });
})();
